import { loadStripe } from "@stripe/stripe-js";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router";
import { Button } from "@/atoms/button";
import { PayPage } from "@/components/pay-page";
import { EmptyState } from "@/components/empty-state";
import { PortalDoor } from "./portal-door";
import { PortalSkeleton } from "./portal-skeleton";
import { PortalView } from "./portal-view";
import { useCoachColour } from "@/design/use-coach-colour";
import {
  useCancelSubscription,
  useCreateSetupIntent,
  usePortalData,
  usePortalInvoices,
  useRequestPortalAccess,
  useSetDefaultPaymentMethod,
} from "@/data/portal";
import type { PortalSubscription } from "@/data/portal";
import type { Stripe } from "@stripe/stripe-js";

/**
 * The billing portal (template detail 1.7 signed in, auth 1.16 at the door).
 *
 * The route owns the token, the queries and the mutations; `PortalView` owns the drawing and
 * takes everything as props, so the signed-in page can be rendered without a token by
 * `/dev/portal` and looked at in every state.
 */

const STRIPE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? "";

export function PortalPage() {
  const { t, i18n } = useTranslation();
  const { stripeCustomerId } = useParams<{ stripeCustomerId: string }>();
  const [searchParams] = useSearchParams();
  const stripeAccountId = searchParams.get("stripeAccountId") ?? "";
  const token = searchParams.get("token") ?? "";
  const action = searchParams.get("action");

  const requestAccess = useRequestPortalAccess();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [doorFailed, setDoorFailed] = useState(false);

  const {
    data,
    isLoading: loadingPortal,
    isError: portalError,
  } = usePortalData(stripeCustomerId, stripeAccountId, token || undefined);
  const { data: invoicesData, isLoading: loadingInvoices } = usePortalInvoices(
    stripeCustomerId,
    stripeAccountId,
    token || undefined,
  );

  const createSetupIntent = useCreateSetupIntent();
  const setDefault = useSetDefaultPaymentMethod(
    stripeCustomerId,
    stripeAccountId,
    token || undefined,
  );
  const cancelSubscription = useCancelSubscription(
    stripeCustomerId,
    stripeAccountId,
    token || undefined,
  );

  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [addingCard, setAddingCard] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState<string | null>(
    null,
  );
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
  const [cardsError, setCardsError] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<PortalSubscription | null>(
    null,
  );
  const [cancelError, setCancelError] = useState<string | null>(null);

  /* Signed in only. The door has no payload and no coach to take the colour from, so it stays
     Gymkee orange; asking the server for a colour before the person has proved who they are
     would also be telling an unauthenticated caller which coach this customer belongs to. */
  const coachHex = useCoachColour(data?.business.brandColor);

  useEffect(() => {
    if (!data?.stripeAccountId) return;
    void loadStripe(STRIPE_KEY, { stripeAccount: data.stripeAccountId }).then(
      setStripe,
    );
  }, [data?.stripeAccountId]);

  const openAdd = useCallback(async () => {
    if (!stripeCustomerId || !stripeAccountId || !token) return;
    setCardsError(null);
    try {
      const result = await createSetupIntent.mutateAsync({
        stripeCustomerId,
        stripeAccountId,
        token,
      });
      setSetupClientSecret(result.clientSecret);
      setAddingCard(true);
    } catch {
      setCardsError(t("gymkeePay.portal.addCardFailed"));
    }
  }, [createSetupIntent, stripeAccountId, stripeCustomerId, token, t]);

  /* `?action=update-payment` is the link a dunning email sends: the form opens straight away. */
  useEffect(() => {
    if (
      action === "update-payment" &&
      data &&
      !addingCard &&
      !setupClientSecret
    ) {
      void openAdd();
    }
  }, [action, data, addingCard, setupClientSecret, openAdd]);

  const language = i18n.resolvedLanguage ?? i18n.language;

  if (!stripeCustomerId || !stripeAccountId) {
    return (
      <PayPage>
        <main>
          <EmptyState
            heading={t("gymkeePay.portal.linkIncomplete")}
          />
        </main>
      </PayPage>
    );
  }

  if (!token) {
    return (
      <PortalDoor
        sending={requestAccess.isPending}
        sentTo={sentTo}
        failed={doorFailed}
        onSend={() => {
          setDoorFailed(false);
          requestAccess
            .mutateAsync({ stripeCustomerId, stripeAccountId })
            .then((result) => setSentTo(result.email))
            .catch(() => setDoorFailed(true));
        }}
      />
    );
  }

  if (loadingPortal || loadingInvoices) return <PortalSkeleton />;

  if (portalError || !data) {
    return (
      <PayPage>
        <main>
          <EmptyState
            heading={t("gymkeePay.portal.loadFailedHeading")}
            action={
              <Button
                variant="secondary"
                onClick={() => window.location.reload()}
              >
                {t("gymkeePay.common.tryAgain")}
              </Button>
            }
          />
        </main>
      </PayPage>
    );
  }

  return (
    <PortalView
      data={data}
      invoices={invoicesData?.invoices ?? []}
      stripe={stripe}
      coachHex={coachHex}
      language={language}
      addingCard={addingCard}
      setupClientSecret={setupClientSecret}
      settingDefaultId={settingDefaultId}
      cardsError={cardsError}
      onOpenAdd={() => void openAdd()}
      onCloseAdd={() => {
        setAddingCard(false);
        setSetupClientSecret(null);
      }}
      onCardAdded={() => {
        setAddingCard(false);
        setSetupClientSecret(null);
        window.location.reload();
      }}
      onSetDefault={(paymentMethodId) => {
        setCardsError(null);
        setSettingDefaultId(paymentMethodId);
        setDefault.mutate(paymentMethodId, {
          onError: () => setCardsError(t("gymkeePay.portal.defaultFailed")),
          onSettled: () => setSettingDefaultId(null),
        });
      }}
      cancelTarget={cancelTarget}
      cancelling={cancelSubscription.isPending}
      cancelError={cancelError}
      onAskCancel={(subscription) => {
        setCancelError(null);
        setCancelTarget(subscription);
      }}
      onCloseCancel={() => setCancelTarget(null)}
      onConfirmCancel={(subscription) => {
        setCancelError(null);
        cancelSubscription.mutate(subscription.id, {
          onSuccess: () => setCancelTarget(null),
          onError: () => setCancelError(t("gymkeePay.portal.cancelFailed")),
        });
      }}
    />
  );
}
